import {
  DefaultError,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { useInfiniteHandler, useQueryToast } from 'hooks';
import i18next from 'i18next';
import { apiClient } from 'utils';
import { getDefaultInfinitePageParam } from 'utils/service/defaultInfinitePageParam';
import { useResponseHandler } from 'utils/service/responseHandler';
import {
  ICreateProvider,
  IProviderResponse,
  IProviderSearchParams,
  IProvidersInfiniteResponse,
  IUpdateProvider,
} from './types';

export const useGetProvidersInfiniteList = (params: IProviderSearchParams) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params.page,
    params.itemCount
  );

  return useInfiniteQuery<IProviderResponse[], DefaultError>({
    queryKey: ['providers-list', params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'Providers',
        params: { ...params, itemCount, page: pageParam },
      });

      const provider = await responseHandler<IProvidersInfiniteResponse>(
        response,
        {
          groupName: `useGetProvidersInfiniteList - ${pageParam}`,
        }
      );

      return infiniteHandler(provider);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const useGetProvider = (id: string) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IProviderResponse, DefaultError>({
    queryKey: ['provider', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Providers/${id}`,
      });

      return responseHandler(response, {
        groupName: 'useGetProviders',
        notFoundPath: '/provider',
      });
    },
  });
};

export const usePostProvider = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePostProviders');

  return useMutation<IProviderResponse, DefaultError, ICreateProvider>({
    mutationFn: async body => {
      loading(
        i18next.t('service-code.create.loading', {
          what: body.name,
        })
      );

      const response = await apiClient({
        method: 'POST',
        url: `Providers`,
        body: body,
      });

      return responseHandler(response, {
        groupName: `usePostProviders`,
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: data.name,
        })
      );
    },
    onError: dismiss,
  });
};

export const usePutProvider = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePutProvider');

  return useMutation<IProviderResponse, DefaultError, IUpdateProvider>({
    mutationFn: async body => {
      loading(
        i18next.t('create.update.loading', {
          what: body.name,
        })
      );

      const response = await apiClient({
        method: 'PUT',
        url: `Providers`,
        body: body,
      });

      return responseHandler(response, {
        groupName: `usePutProvider`,
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.update.success', {
          what: data.name,
        })
      );
    },
    onError: dismiss,
  });
};

export const useDeleteProvider = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useDeleteProvider');

  return useMutation<boolean, DefaultError, string>({
    mutationFn: async (id: string) => {
      loading(
        i18next.t('service-message.remove.loading', {
          what: i18next.t('provider.what'),
        })
      );

      const response = await apiClient({
        method: 'DELETE',
        url: `Providers/${id}`,
      });

      return responseHandler(response, {
        groupName: `useDeleteProvider`,
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.remove.success', {
          what: i18next.t('provider.what'),
        })
      );
    },
    onError: dismiss,
  });
};
